import React, { useContext, useMemo, useState } from "react";
import Styles from "./UserProfileInfo.module.scss";
import Flex from "@vahak/core-ui/dist/layout/Flex";
import AvatarImage from "../../utils/avatar-image/AvatarImage";
import Typography from "@vahak/core-ui/dist/components/Typography";
import TextCTA from "@vahak/core-ui/dist/components/TextCTA";
import UserRating from "./user-rating/UserRating";

//Icons
import BankVerifiedIcon from "@vahak/core/dist/icons/Rupee-Green.svg";
import VerifiedIcon from "@vahak/core/dist/icons/verified-tick.svg";
import MembershipTag from "@vahak/core/dist/icons/VahakMember.svg";
import Phone from "@vahak/core/dist/icons/Phone.svg";

import { CompanyServiceType, CompanyServiceTypeMap } from "@vahak/core/dist/constants";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import { useRouter } from "next/router";
import { ROUTES } from "@vahak/core/dist/constants";
import { AppContext } from "@vahak/core/dist/app-context";
import { useCsDashboard } from "@vahak/core/dist/hooks/useCsDashboard";
import { ADMIN_OPERATION } from "../../../constants/adminOperation";
import IfCanAccess from "../../admin/IfCanAccess/IfCanAccess";
import { useClickToCall } from "../../../context/call/ClickToCallAppProvider";
import { CallInitiationParams } from "../../../services/call/useCallInitiation";
import { formattedPhoneNumber } from "@vahak/core-ui/dist/methods/string";
import { useEventTrackerService } from "@vahak/core/dist/_services/hooks/useEventTrackerService";
interface UserProfileInfoProps {
    id?: string;
    companyId: number;
    companyLogo: string;
    name: string;
    isBankVerified: boolean;
    isVerified: boolean;
    isAadhaarVerified: boolean;
    isGstVerified: boolean;
    isPanVerified: boolean;
    isMember: boolean;
    rating: number;
    serviceType: string;
    handleClickProfile?: () => void;
    phoneNumber?: string;
    agentTagged?: boolean;
    clickToCallMetaInfo?: CallInitiationParams;
    revealCompanyInfo?: boolean;
    requireReveal?: boolean;
    revealTarget?: string;
    onReveal?: () => void;
}

const UserProfileInfo = ({
    id,
    companyId,
    companyLogo,
    name,
    isBankVerified,
    isVerified,
    rating,
    isAadhaarVerified,
    isGstVerified,
    isPanVerified,
    isMember,
    serviceType,
    handleClickProfile,
    phoneNumber,
    agentTagged,
    clickToCallMetaInfo = {},
    revealCompanyInfo = false,
    requireReveal = true,
    revealTarget,
    onReveal
}: UserProfileInfoProps) => {
    const router = useRouter();
    const {
        setHistoryLocation,
        companyId: agentCompanyId,
        name: agentCompanyName,
        route
    } = useContext(AppContext);
    const { checkPermission, isTso } = useCsDashboard();
    const clickToCallContext = useClickToCall();
    const { sendGAandMoEngageEvent } = useEventTrackerService();
    const [isRevealed, setIsRevealed] = useState(false);
    const showSensitiveInfo = !requireReveal || isRevealed;

    const resolvedCallParams = useMemo(
        () => ({
            ...clickToCallMetaInfo,
            callee_id: clickToCallMetaInfo.callee_id ?? companyId
        }),
        [clickToCallMetaInfo, companyId]
    );

    const ctaSlot = clickToCallContext?.registerCallCta(
        {
            number: `${phoneNumber}`,
            imgUrl: companyLogo,
            name: name
        },
        resolvedCallParams
    );
    const onClickProfile = () => {
        if (!showSensitiveInfo || isTso) {
            return;
        }
        handleClickProfile?.();
        setHistoryLocation?.(router.asPath);
        router.push(`${ROUTES.transporterProfile.getUrlWithId(companyId)}`);
    };

    const handleReveal = () => {
        setIsRevealed(true);
        if (onReveal) {
            onReveal();
            return;
        }
        sendGAandMoEngageEvent({
            name: "reveal_info",
            data: {
                source_screen: route,
                reveal_target: revealTarget,
                ticket_id: clickToCallMetaInfo.ticket_id,
                load_id: clickToCallMetaInfo.load_id,
                bid_id: clickToCallMetaInfo.bid_id,
                lorry_id: clickToCallMetaInfo.lorry_id,
                agent_company_id: agentCompanyId,
                agent_company_name: agentCompanyName
            }
        });
    };

    const restrictedView = checkPermission(ADMIN_OPERATION.tagAgentManagerUI);
    const showPhoneNumber = restrictedView ? !!agentTagged : true;

    const PhoneNumberSlot = (
        <IfCanAccess action={ADMIN_OPERATION.userPhoneNumberVisibilityUI} alsoCheck={!!phoneNumber && showPhoneNumber}>
            <Flex alignItems="center" gap={10}>
                {showSensitiveInfo ? (
                    <>
                        <span className={Styles.phoneIcon}>
                            <Phone />
                        </span>
                        <Typography weight="semibold" size="xs" color={COLORS.GREY_700}>
                            {formattedPhoneNumber(phoneNumber)}
                        </Typography>
                    </>
                ) : (
                    <TextCTA onClick={handleReveal} weight="medium" underline color={COLORS.RED}>
                        Reveal Info
                    </TextCTA>
                )}
                {ctaSlot}
            </Flex>
        </IfCanAccess>
    );
    return (
        <Flex className={Styles.main} alignItems="center" gap={rating ? 16 : 6}>
            {revealCompanyInfo && (
                <div
                    className={Styles.logoWrapper}
                    id={`${id}--companyLogo`}
                    onClick={showSensitiveInfo ? onClickProfile : undefined}
                >
                    <AvatarImage className={Styles.logo} imageUrl={companyLogo} firstName={name} size="s" />
                    <div className={Styles.rating}>{Boolean(rating) ? <UserRating rating={rating} /> : <></>}</div>
                </div>
            )}

            <div className={Styles.company}>
                {revealCompanyInfo && (
                    <Typography
                        id={`${id}--companyName`}
                        weight="semibold"
                        size="sm"
                        color={COLORS.GREY_900}
                        data-one-line-text
                        title={name}
                    >
                        {name}
                    </Typography>
                )}
                <div className={Styles.companyDesc}>
                    {!!isBankVerified && <BankVerifiedIcon />}
                    {!!(isVerified || isAadhaarVerified || isGstVerified || isPanVerified) && !isMember && (
                        <VerifiedIcon />
                    )}
                    {!!isMember && <MembershipTag />}
                    <Typography
                        weight="semibold"
                        size="xs"
                        color={COLORS.GREY_700}
                        id={`${id}--serviceType`}
                        data-service-type
                        data-one-line-text
                        title={CompanyServiceTypeMap[serviceType as CompanyServiceType]}
                    >
                        {CompanyServiceTypeMap[serviceType as CompanyServiceType]}
                    </Typography>
                </div>
                {PhoneNumberSlot}
            </div>
        </Flex>
    );
};

export default UserProfileInfo;
